import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { PaymentMethod } from '@web-solutions/core/store/billing';

import { selectPaymentMethod } from 'core/store/billing/selectors';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { OneTimeProduct } from 'core/interfaces/billing';

import { OneTimePaymentArgs } from 'core/one-time/constans/types';
import { NATIVE_PAYMENT, PAYMENT_MODES } from 'core/one-time/constans';
import { OneClickPaymentRef } from 'core/one-time/one-click';

interface UsePaymentMethodsProps {
  ocRef: React.RefObject<OneClickPaymentRef | null>;
  trigger: string;
  metadata: any;
  solidMetadata: any;
  activeProduct?: OneTimeProduct;
  onShowPaymentPopup: () => void;
}

export const usePaymentMethods = ({
  ocRef,
  trigger,
  metadata,
  solidMetadata,
  activeProduct,
  onShowPaymentPopup,
}: UsePaymentMethodsProps) => {
  const { oneClickPaymentMode } = useRemoteConfig();
  const paymentMethod = useSelector(selectPaymentMethod) as PaymentMethod;

  const handleProductNativePayment = useCallback(
    (args: Record<string, any>) => {
      try {
        NATIVE_PAYMENT[paymentMethod]?.(args);
      } catch (error) {
        onShowPaymentPopup();
      }
    },
    [paymentMethod, onShowPaymentPopup]
  );

  const handleProductNativeModalPayment = useCallback(
    (args: Record<string, any>) => {
      try {
        NATIVE_PAYMENT[paymentMethod]?.(args);
      } catch (error) {
        console.log('[ERROR]', error);
      }
      onShowPaymentPopup();
    },
    [paymentMethod, onShowPaymentPopup]
  );

  const executePaymentByMode = useCallback(
    (args: OneTimePaymentArgs) => {
      switch (oneClickPaymentMode) {
        case PAYMENT_MODES.NATIVE_MODAL:
          return handleProductNativeModalPayment(args);
        case PAYMENT_MODES.NATIVE:
          return handleProductNativePayment(args);
        case PAYMENT_MODES.MODAL:
          return onShowPaymentPopup();
        default:
          return ocRef.current?.createOneClickPayment({
            ...args,
            trigger,
            metadata,
            solidMetadata,
          });
      }
    },
    [
      oneClickPaymentMode,
      handleProductNativeModalPayment,
      handleProductNativePayment,
      activeProduct?.id,
      activeProduct?.currency,
      activeProduct?.amount,
      trigger,
      ocRef,
      metadata,
      solidMetadata,
      onShowPaymentPopup,
    ]
  );

  return {
    paymentMethod,
    oneClickPaymentMode,
    handleProductNativePayment,
    handleProductNativeModalPayment,
    executePaymentByMode,
  };
};
